import { z } from 'zod';
import { CorpusStatusSchema } from './enums.js';

/**
 * The version a document carries in its own text ("3.1.0" for "Acceptable Use
 * Policy V3.1.0") — what an auditor recognises, as opposed to `currentVersion`,
 * the application's revision counter. Parsed from the title and content on
 * every save (`parseDocumentVersion` in @grc/markdown) unless given explicitly;
 * '' when the document names no version.
 */
export const DocumentVersionInputSchema = z.string().trim().max(40);

/** Explicit input → stored label: a leading "V" before a digit is dropped ("V3.1.0" → "3.1.0"). */
export function normaliseDocumentVersion(input: string): string {
  return input.trim().replace(/^v\s?(?=\d)/i, '').slice(0, 40);
}

/**
 * What to show for a version: the document's own label ("V3.1.0") when it has
 * one, else the application revision ("v2").
 */
export function versionLabel(documentVersion: string, versionNumber: number): string {
  if (documentVersion === '') return `v${versionNumber}`;
  return /^\d/.test(documentVersion) ? `V${documentVersion}` : documentVersion;
}

/** A "V3.1.0" / "v3" suffix on a title, as the parser in @grc/markdown recognises it. */
const TITLE_VERSION_SUFFIX = /\bV\s?(\d+(?:\.\d+){0,3})\s*$/i;

/** Numeric part by part, so "3" is the same version as "3.0.0" and "1.10" is not "1.1". */
function sameVersion(a: string, b: string): boolean {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return false;
  return true;
}

/**
 * A title with its version for a heading, a dropdown option or a spreadsheet
 * column: "Acceptable Use Policy (V3.1.0)". When the title already ends with
 * that version ("Acceptable Use Policy V3.1.0", the convention of most
 * libraries) the title alone is returned, never "… V3.1.0 (V3.1.0)".
 */
export function titleWithVersion(title: string, documentVersion: string, versionNumber: number): string {
  const suffix = TITLE_VERSION_SUFFIX.exec(title);
  if (suffix && /^\d/.test(documentVersion) && sameVersion(suffix[1]!, documentVersion)) return title.trim();
  return `${title} (${versionLabel(documentVersion, versionNumber)})`;
}

export const CorpusItemSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  key: z.string(),
  title: z.string(),
  description: z.string(),
  contentMarkdown: z.string(),
  currentVersion: z.number().int(),
  documentVersion: z.string(),
  status: CorpusStatusSchema,
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CorpusItem = z.infer<typeof CorpusItemSchema>;

export const CorpusItemSummarySchema = CorpusItemSchema.omit({ contentMarkdown: true });
export type CorpusItemSummary = z.infer<typeof CorpusItemSummarySchema>;

export const CreateCorpusItemInputSchema = z.object({
  key: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9-]*$/),
  title: z.string().min(1).max(300),
  description: z.string().max(2000).default(''),
  contentMarkdown: z.string().default(''),
  /** Omit to parse it from the title and content. */
  documentVersion: DocumentVersionInputSchema.optional(),
});
export type CreateCorpusItemInput = z.infer<typeof CreateCorpusItemInputSchema>;

export const UpdateCorpusItemInputSchema = z.object({
  title: z.string().min(1).max(300).optional(),
  description: z.string().max(2000).optional(),
  contentMarkdown: z.string().optional(),
  status: CorpusStatusSchema.optional(),
  changeSummary: z.string().max(1000).optional(),
  /** Omit to re-parse it whenever the title or content changes; '' clears it. */
  documentVersion: DocumentVersionInputSchema.optional(),
});
export type UpdateCorpusItemInput = z.infer<typeof UpdateCorpusItemInputSchema>;

export const CorpusItemVersionSchema = z.object({
  id: z.string(),
  corpusItemId: z.string(),
  versionNumber: z.number().int(),
  /** The document's own version label at this revision ('' when it named none). */
  documentVersion: z.string(),
  contentMarkdown: z.string(),
  authorId: z.string(),
  changeSummary: z.string(),
  createdAt: z.string(),
});
export type CorpusItemVersion = z.infer<typeof CorpusItemVersionSchema>;

/**
 * Result of the blank-document dropzone (POST /corpus/:id/import): content
 * either extracted from the uploaded document(s) or, when nothing relevant
 * was identified, AI-drafted from best practice guidelines.
 */
export const CorpusItemImportResultSchema = z.object({
  action: z.enum(['imported', 'generated']),
  files: z.array(z.string()),
  item: CorpusItemSchema,
});
export type CorpusItemImportResult = z.infer<typeof CorpusItemImportResultSchema>;
